/**
 * WebSocket event types emitted by the backend.
 *
 * CHAT                     – bidirectional; is_owner distinguishes operator vs customer message
 * CONVERSATION_CREATED     – new conversation, always TO_ASSIGN (Scenarios 1, 5)
 * CONVERSATION_UPDATED     – any state change: assignment, revoke, lifecycle (Scenarios 2-6)
 * CONVERSATION_SESSION_CHANGED – 24-hour messaging window status (handling operator only)
 * AI_INSIGHTS_UPDATED      – audio transcription / plate detection result (handling operator only)
 */
export enum WSMessageType {
  CHAT = "chat",
  CONVERSATION_CREATED = "conversation.created",
  CONVERSATION_UPDATED = "conversation.updated",
  CONVERSATION_SESSION_CHANGED = "conversation.session_changed",
  AI_INSIGHTS_UPDATED = "message.ai_insights_updated",
}

export type AiInsightStatus = 'PENDING' | 'COMPLETE' | 'FAILED';

export interface AudioTranscriptionInsight {
  status: AiInsightStatus;
  result: string | null;
  error: string | null;
}

export interface PlateDetectionInsight {
  status: AiInsightStatus;
  result: string[];
  error: string | null;
}

export interface AiInsights {
  audioTranscription?: AudioTranscriptionInsight;
  plateDetection?: PlateDetectionInsight;
}

export interface WsMessage {
  type: string;
  content: any;
  errorContent?: any;
  isErrorMessage: boolean;
}

export enum MessageType {
  SENT = "S",
  RECEIVED = "R",
}

export enum MessageMimeType {
  TEXT = "text/plain",
  IMAGE = "image/*",
  VIDEO = "video/*",
  AUDIO = "audio/*",
  DOCUMENT = "application/*",
}

export enum MessageStatus {
  SENT = "SENT",           // sent from client, waiting server confirmation
  RECEIVED = "RECEIVED",   // server processed successfully
  DELIVERED = "DELIVERED", // recipient device got it
  READ = "READ",           // recipient read it
  FAILED = "FAILED",       // error in sending/processing
}

export interface MessageOperator {
  id: number;
  firstName?: string;
  lastName?: string;
}

export interface Message {
  id?: number;
  creationDate?: string;
  text?: string;
  type: MessageType;
  isRead?: boolean;
  messageId?: string;
  fileName?: string;
  mimeType?: MessageMimeType;
  caption?: string;
  userId?: number;
  conversationId: number;
  messageUuid: string;
  status?: MessageStatus;
  isOwner?: boolean;
  attachment?: any;
  mediaUrl?: string;
  isQuote?: boolean;
  replyMessage?: Message;
  replyMessageId?: number;
  replyContext?: {};
  failedToSend?: boolean;
  aiInsights?: AiInsights | null;
  operator?: MessageOperator | null;
  note?: string | null;
}
